const ascii = (bytes, start, length) => String.fromCharCode(...bytes.slice(start, start + length));
function stripJpegIcc(bytes) {
    if (bytes[0] !== 0xff || bytes[1] !== 0xd8)
        return bytes;
    const segments = [bytes.slice(0, 2)];
    let offset = 2;
    let changed = false;
    while (offset + 4 <= bytes.length) {
        if (bytes[offset] !== 0xff)
            return bytes;
        const marker = bytes[offset + 1];
        if (marker === 0xda) {
            segments.push(bytes.slice(offset));
            offset = bytes.length;
            break;
        }
        const length = ((bytes[offset + 2] ?? 0) << 8) | (bytes[offset + 3] ?? 0);
        if (length < 2 || offset + 2 + length > bytes.length)
            return bytes;
        const end = offset + 2 + length;
        const appPayload = ascii(bytes, offset + 4, Math.min(32, end - offset - 4));
        const isForbiddenMetadata = (marker === 0xe2 && appPayload.startsWith('ICC_PROFILE\0')) ||
            (marker === 0xe1 &&
                (appPayload.startsWith('Exif\0\0') ||
                    appPayload.startsWith('http://ns.adobe.com/xap/1.0/')));
        if (isForbiddenMetadata)
            changed = true;
        else
            segments.push(bytes.slice(offset, end));
        offset = end;
    }
    if (!changed || offset !== bytes.length)
        return bytes;
    return concat(segments);
}
function stripWebpIcc(bytes) {
    if (bytes.length < 12 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP')
        return bytes;
    const chunks = [];
    let offset = 12;
    let changed = false;
    while (offset + 8 <= bytes.length) {
        const size = new DataView(bytes.buffer, bytes.byteOffset + offset + 4, 4).getUint32(0, true);
        const end = offset + 8 + size + (size % 2);
        if (end > bytes.length)
            return bytes;
        const kind = ascii(bytes, offset, 4);
        if (kind === 'ICCP' || kind === 'EXIF' || kind === 'XMP ')
            changed = true;
        else {
            const chunk = bytes.slice(offset, end);
            if (kind === 'VP8X' && chunk.length >= 9)
                chunk[8] = (chunk[8] ?? 0) & ~0x20;
            chunks.push(chunk);
        }
        offset = end;
    }
    if (!changed || offset !== bytes.length)
        return bytes;
    const body = concat(chunks);
    const output = new Uint8Array(12 + body.length);
    output.set(bytes.slice(0, 12));
    output.set(body, 12);
    new DataView(output.buffer).setUint32(4, output.length - 8, true);
    return output;
}
function stripPngMetadata(bytes) {
    const signature = '\x89PNG\r\n\x1a\n';
    if (bytes.length < 8 || ascii(bytes, 0, 8) !== signature)
        return bytes;
    const chunks = [bytes.slice(0, 8)];
    let offset = 8;
    let changed = false;
    while (offset + 12 <= bytes.length) {
        const size = new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0, false);
        const end = offset + 12 + size;
        if (end > bytes.length)
            return bytes;
        const kind = ascii(bytes, offset + 4, 4);
        const payload = ascii(bytes, offset + 8, Math.min(size, 48));
        const forbidden = kind === 'iCCP' ||
            kind === 'eXIf' ||
            ((kind === 'iTXt' || kind === 'tEXt' || kind === 'zTXt') &&
                (payload.includes('XML:com.adobe.xmp') || payload.includes('Raw profile type exif')));
        if (forbidden)
            changed = true;
        else
            chunks.push(bytes.slice(offset, end));
        offset = end;
        if (kind === 'IEND')
            break;
    }
    if (!changed || offset !== bytes.length)
        return bytes;
    return concat(chunks);
}
function concat(parts) {
    const output = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
    let offset = 0;
    for (const part of parts) {
        output.set(part, offset);
        offset += part.length;
    }
    return output;
}
/** Removes browser-added ICC containers without retaining EXIF/GPS or XMP metadata. */
export async function stripDerivativeColorProfile(blob) {
    const source = new Uint8Array(await blob.arrayBuffer());
    const stripped = blob.type === 'image/jpeg'
        ? stripJpegIcc(source)
        : blob.type === 'image/webp'
            ? stripWebpIcc(source)
            : blob.type === 'image/png'
                ? stripPngMetadata(source)
                : source;
    return stripped === source
        ? blob
        : new Blob([
            stripped.buffer.slice(stripped.byteOffset, stripped.byteOffset + stripped.byteLength),
        ], {
            type: blob.type,
        });
}
